export const GALLERY_BUCKET = "the-clash-28c53.firebasestorage.app";

export const PHOTOS_PREFIX = "tournament-photos";
export const DERIVED_PREFIX = "gallery-derived";
export const MANIFEST_PREFIX = "gallery-manifest";
export const MANIFEST_VERSION = 1;

export const INDEX_MANIFEST_PATH = `${MANIFEST_PREFIX}/index.json`;

export const CACHE_IMMUTABLE = "public, max-age=31536000, immutable";
export const CACHE_MANIFEST = "public, max-age=60";

export type DerivativeKind = "thumb" | "web";

export interface GalleryPhotoEntry {
  path: string;
  folder: string;
  timeCreated: string;
  cover: boolean;
}

export interface GalleryAlbumManifest {
  version: number;
  album: string;
  generatedAt: string;
  photos: GalleryPhotoEntry[];
}

export interface GalleryAlbumEntry {
  name: string;
  count: number;
  cover: string | null;
  latest: string;
}

export interface GalleryIndexManifest {
  version: number;
  generatedAt: string;
  albums: GalleryAlbumEntry[];
}

// Public read is enabled on this bucket, so a download URL is derivable from
// the object path — no getDownloadURL round trip per photo.
export const storageUrl = (path: string): string =>
  `https://firebasestorage.googleapis.com/v0/b/${GALLERY_BUCKET}/o/${encodeURIComponent(path)}?alt=media`;

export const derivedPath = (objectPath: string, kind: DerivativeKind): string => {
  const rest = objectPath.startsWith(`${PHOTOS_PREFIX}/`)
    ? objectPath.slice(PHOTOS_PREFIX.length + 1)
    : objectPath;
  const slash = rest.lastIndexOf("/");
  const dot = rest.lastIndexOf(".");
  const stem = dot > slash ? rest.slice(0, dot) : rest;
  return `${DERIVED_PREFIX}/${kind}/${stem}.webp`;
};

export const derivedUrl = (objectPath: string, kind: DerivativeKind): string =>
  storageUrl(derivedPath(objectPath, kind));

export const albumOf = (objectPath: string): string | null => {
  const parts = objectPath.split("/");
  if (parts[0] !== PHOTOS_PREFIX || parts.length < 3) return null;
  return parts[1];
};

export const folderOf = (objectPath: string): string => {
  const parts = objectPath.split("/");
  if (parts[0] !== PHOTOS_PREFIX || parts.length < 3) return "";
  return parts.slice(2, -1).join("/");
};

// A cover is the file the dashboard renames to "thumbnail.<ext>". Uploaded
// files always carry a "<timestamp>-" prefix, so a prefix match cannot collide
// with a user file that merely contains the word.
export const isCoverName = (fileName: string): boolean => {
  const lower = fileName.toLowerCase();
  return lower.startsWith("thumbnail") || lower.startsWith("cover");
};

export const albumManifestPath = (album: string): string =>
  `${MANIFEST_PREFIX}/albums/${album}.json`;
